import type { ComputedRef, Ref } from "vue";
import type { effectiveDifferentialPressureUnit } from "@/libs/units/pressure";
import type { ScenarioCalculations } from "../calculationTypes";
import { EquipmentCalculatorSectionKey } from "../calculationTypes";
import type { ConverterInputProps } from "@/components/types";
import type {
  OperatingParameters,
  WellProperties
} from "@/composables/forms/formTypes";
import type { useScenarioUnits } from "@/composables/units/useScenarioUnits";

export type Section = {
  id: EquipmentCalculatorSectionKey;
  hash: string; // Hash is unique for sizing parameters sections. Goes in the URL
  label: string;
  routeName: string;
  aggregateDisplays: ConverterInputProps[];
  individualDisplays?: {
    label: string;
    displays: ConverterInputProps[];
  }[];
};

export enum ScenarioDetailSectionKey {
  PUMP = "pump",
  MOTOR = "motor",
  INTAKE = "intakes",
  CABLE = "cables",
  FLUID_PROPERTIES = "fluidProperties",
  GAS_PROPERTIES = "gasProperties",
  WELL_COMPLETION = "wellCompletion",
  WELL_TEST = "wellTest",
  SEAL_SENSOR_MLE = "sealSensorMle",
  THRUST_CHAMBER = "thrustChamber",
  SURFACE_DRIVE = "surfaceDrive",
  OPERATING_PARAMETERS = "operatingParameters"
}

export enum ScenarioDetailSectionTitle {
  PUMP = "Pumps",
  MOTOR = "Motor",
  HPS_MOTOR = "HPS Motor",
  INTAKE = "Intakes",
  CABLE = "Cables",
  FLUID_PROPERTIES = "Fluid Properties",
  WELL_TEST = "Well Test",
  SEAL_SENSOR_MLE = "Seal",
  THRUST_CHAMBER = "Thrust Chamber",
  MLE = "MLE",
  SURFACE_DRIVE = "Surface Drive"
}

export const EquipmentWithNoCalculations = [
  EquipmentCalculatorSectionKey.OPERATING_PARAMETERS,
  EquipmentCalculatorSectionKey.WELL_COMPLETION
];

export const HIDDEN_WHEN_HPS = [
  EquipmentCalculatorSectionKey.INTAKE_CONDITIONS,
  EquipmentCalculatorSectionKey.CABLE,
  EquipmentCalculatorSectionKey.MLE,
  EquipmentCalculatorSectionKey.SEAL,
  EquipmentCalculatorSectionKey.MOTOR
];

export const HIDDEN_WHEN_ESP = [
  EquipmentCalculatorSectionKey.THRUST_CHAMBER,
  EquipmentCalculatorSectionKey.HPS_MOTOR
];

type ScenarioUnits = ReturnType<typeof useScenarioUnits>;

// Everything a section builder can read. Builders are called inside a
// computed, so reactive reads in them are still tracked.
export type SectionDeps = Pick<
  ScenarioUnits,
  | "getHydraulicPowerUnit"
  | "getRealPowerUnit"
  | "getCurrentUnit"
  | "getGasFlowUnit"
  | "getVolumeUnit"
  | "getPressureUnit"
> & {
  scenarioCalculations: ScenarioCalculations;
  parameters: Ref<OperatingParameters>;
  wellProperties: Ref<WellProperties>;
  differentialPressureTargetUnit: ComputedRef<
    ReturnType<typeof effectiveDifferentialPressureUnit>
  >;
};
